import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import {
  ORIGIN_LABELS,
  REQUEST_STATUS_LABELS,
  useRequestCounts,
  type ProductRequestFilters,
  type RequestOrigin,
  type RequestStatus,
} from '@/hooks/useProductRequests'
import { cn } from '@/lib/utils'

const STATUS_TABS: { value: RequestStatus | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'OPEN', label: REQUEST_STATUS_LABELS.OPEN },
  { value: 'NEED_TO_SOURCE', label: REQUEST_STATUS_LABELS.NEED_TO_SOURCE },
  { value: 'SOURCED', label: REQUEST_STATUS_LABELS.SOURCED },
  { value: 'ADDED_TO_PO', label: REQUEST_STATUS_LABELS.ADDED_TO_PO },
  { value: 'ORDERED', label: REQUEST_STATUS_LABELS.ORDERED },
  { value: 'CUSTOMER_NOTIFIED', label: REQUEST_STATUS_LABELS.CUSTOMER_NOTIFIED },
  { value: 'PURCHASED', label: REQUEST_STATUS_LABELS.PURCHASED },
  { value: 'CLOSED_NO_PURCHASE', label: REQUEST_STATUS_LABELS.CLOSED_NO_PURCHASE },
]

// Stored as display names, not usernames — the spec's Assignment section lists
// "Gina, Rachel, or Olivia" and the backend round-trips the string as-is.
const ASSIGNEES = ['Gina', 'Rachel', 'Olivia']

const ORIGINS: RequestOrigin[] = ['BREWSTER', 'PHONE_EMAIL', 'EVENT']

interface RequestFiltersProps {
  filters: ProductRequestFilters
  onChange: (patch: Partial<ProductRequestFilters>) => void
  /** Rows currently rendered — the page already has the list, so it passes the number down. */
  resultCount: number
}

export function RequestFilters({ filters, onChange, resultCount }: RequestFiltersProps) {
  const { data: counts } = useRequestCounts()
  const allCount = counts
    ? Object.values(counts).reduce((sum, n) => sum + n, 0)
    : undefined

  const [localSearch, setLocalSearch] = useState(filters.search ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setLocalSearch(filters.search ?? '')
  }, [filters.search])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setLocalSearch(val)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onChange({ search: val || undefined })
      }, 200)
    },
    [onChange]
  )

  const clearSearch = useCallback(() => {
    setLocalSearch('')
    onChange({ search: undefined })
  }, [onChange])

  return (
    <div className="border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
      {/* Status tabs */}
      <div className="flex h-9 items-stretch border-b border-[var(--line-warm)] overflow-x-auto">
        {STATUS_TABS.map((tab) => {
          const isActive = filters.status === tab.value
          const count = tab.value ? counts?.[tab.value] : allCount
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => onChange({ status: tab.value })}
              className={cn(
                'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors whitespace-nowrap',
                isActive
                  ? 'text-[var(--text-primary)] font-semibold'
                  : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
              )}
              style={isActive ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' } : undefined}
            >
              {tab.label}
              {count !== undefined && (
                <span className="text-[10px] text-[var(--mute)]">{count}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Toolbar: result count, search, assignee, source, date range */}
      <div className="flex items-center gap-2 px-4 py-2">
        <div className="text-[11px] text-[var(--mute)]" aria-live="polite">
          {resultCount} {resultCount === 1 ? 'result' : 'results'}
        </div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative max-w-[260px] flex-1">
          <Input
            placeholder="Search customer, product, email…"
            value={localSearch}
            onChange={handleSearchChange}
            className="h-7 pr-6 text-xs"
          />
          {localSearch && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--mute)] hover:text-[var(--text-primary)]"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Assigned To filter */}
        <Select
          value={filters.assigned_to ?? 'all'}
          onValueChange={(v) => onChange({ assigned_to: v === 'all' ? undefined : v })}
        >
          <SelectTrigger
            size="sm"
            className={cn(
              'h-7 w-[140px] text-xs',
              filters.assigned_to && 'border-[var(--text-primary)] text-[var(--text-primary)]'
            )}
            aria-label="Filter by assignee"
          >
            <span data-slot="select-value">{filters.assigned_to ?? 'All assignees'}</span>
          </SelectTrigger>
          {/* `position="popper"` anchors the menu to the trigger; Radix Select
              otherwise defaults to `item-aligned` and can render far off-page. */}
          <SelectContent position="popper" align="end" sideOffset={4}>
            <SelectItem value="all">All assignees</SelectItem>
            {ASSIGNEES.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Source filter */}
        <Select
          value={filters.originated_from ?? 'all'}
          onValueChange={(v) =>
            onChange({
              originated_from: v === 'all' ? undefined : (v as RequestOrigin),
            })
          }
        >
          <SelectTrigger
            size="sm"
            className={cn(
              'h-7 w-[150px] text-xs',
              filters.originated_from && 'border-[var(--text-primary)] text-[var(--text-primary)]'
            )}
            aria-label="Filter by source"
          >
            <span data-slot="select-value">
              {filters.originated_from ? ORIGIN_LABELS[filters.originated_from] : 'All sources'}
            </span>
          </SelectTrigger>
          <SelectContent position="popper" align="end" sideOffset={4}>
            <SelectItem value="all">All sources</SelectItem>
            {ORIGINS.map((o) => (
              <SelectItem key={o} value={o}>
                {ORIGIN_LABELS[o]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date range — native pickers; the wire format is ISO, which is what
            <input type="date"> already speaks, so no conversion here. */}
        <input
          type="date"
          value={filters.date_from ?? ''}
          onChange={(e) => onChange({ date_from: e.target.value || undefined })}
          aria-label="Requests from date"
          className={cn(
            'h-7 rounded-[2px] border border-[var(--line-input)] bg-transparent px-2 text-xs text-[var(--text-primary)]',
            filters.date_from && 'border-[var(--text-primary)]'
          )}
        />
        <span className="text-[11px] text-[var(--mute)]">to</span>
        <input
          type="date"
          value={filters.date_to ?? ''}
          onChange={(e) => onChange({ date_to: e.target.value || undefined })}
          aria-label="Requests to date"
          className={cn(
            'h-7 rounded-[2px] border border-[var(--line-input)] bg-transparent px-2 text-xs text-[var(--text-primary)]',
            filters.date_to && 'border-[var(--text-primary)]'
          )}
        />
      </div>
    </div>
  )
}
